import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject, OnInit, signal } from '@angular/core';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { AbModule } from '@df/module-ab/ab.module';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { CarouselSlideDirective } from '@df/ui-carousel/carousel-slide.directive';
import { CarouselComponent } from '@df/ui-carousel/carousel.component';
import { CarouselDotsComponent } from '@df/ui-carousel/dots/carousel-dots.component';
import { GridColumnDirective } from '@df/ui/atomic/atom/grid/grid-column.directive';
import { GridDirective } from '@df/ui/atomic/atom/grid/grid.directive';
import { AfterContentInitDirective } from '@df/ui/common/after-content-init.directive';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { GSAPDirective } from '@df/ui/display/gsap/gsap.directive';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent } from '@icms/core/content';
import { CmsOutletBlockComponent } from '../../../../../cms/outlet/block/cms-outlet-block.component';
import { BreadcrumbsComponent } from '../../../../../ui/common/breadcrumbs.component';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { AddToBasketComponent } from '../../../basket/add-to-basket';
import { RelatedProductsComponent } from '../../../related-products/related-products.component';
import { ProductConfigurableOptionsSwatchComponent } from '../../configurable-options/swatch/product-configurable-options-swatch.component';
import { PageComponentCountdownBannerComponent } from '../../countdown-banner/countdown-banner.component';
import { ProductDetailsCarouselComponent } from '../../details-carousel/product-details-carousel.component';
import { ProductDiscountLabelComponent } from '../../discount-label/product-discount-label.component';
import { ProductMobileFooterComponent } from '../../mobile-basket/product-mobile-footer.component';
import { ProductBundleOptionsComponent } from '../../product-bundle-options.component';
import { ProductMediaComponent } from '../../product-media.component';
import { ProductViewDetailsComponent } from '../details/product-view-details.component';
import { ProductViewMediaItemComponent } from '../media/item/product-view-media-item.component';
import { ProductViewLightboxComponent } from '../media/lightbox/product-view-lightbox.component';
import { ProductViewNameComponent } from '../name/product-view-name.component';
import { ProductViewShareComponent } from '../share/product-view-share.component';
import { ProductViewSidebarComponent } from '../sidebar/product-view-sidebar.component';
import { ProductViewSummaryComponent } from '../summary/product-view-summary.component';
import { ProductViewTrayComponent } from '../tray/product-view-tray.component';
import { ProductViewWishlistComponent } from '../wishlist/product-view-wishlist.component';
import { ProductViewLayoutAbstract } from './product-view-layout.abstract';
import { PRODUCT_VIEW_LAYOUT_CONFIG } from './product-view-layout.config';
import { IProductViewLayoutContent } from './product-view-layout.interface';
import { ProductViewUspBlockComponent } from '../usp-block/product-view-usp-block.component';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            giftboxesEnabled: boolean;
        }
    }
}

/**
 * Component definition
 */
@Component({
    selector: 'product-view-layout',
    templateUrl: './product-view-layout.component.html',
    styleUrl: './product-view-layout.component.scss',
    standalone: true,
    imports: [
        AbModule,
        forwardRef(() => AddToBasketComponent),
        AfterContentInitDirective,
        BreadcrumbsComponent,
        CmsOutletBlockComponent,
        DfImageDirective,
        CarouselComponent,
        CarouselSlideDirective,
        CarouselDotsComponent,
        ElementHeightDirective,
        GridDirective,
        GridColumnDirective,
        GSAPDirective,
        ICmsClientModule,
        LocalVimeoPlayerComponent,
        NgClass,
        NgTemplateOutlet,
        PageComponentCountdownBannerComponent,
        ProductViewUspBlockComponent,
        PhotoSwipeModule,
        ProductBundleOptionsComponent,
        ProductDiscountLabelComponent,
        forwardRef(() => ProductConfigurableOptionsSwatchComponent),
        ProductDetailsCarouselComponent,
        ProductMediaComponent,
        forwardRef(() => ProductMobileFooterComponent),
        forwardRef(() => ProductViewDetailsComponent),
        ProductViewLightboxComponent,
        ProductViewMediaItemComponent,
        forwardRef(() => ProductViewNameComponent),
        ProductViewShareComponent,
        forwardRef(() => ProductViewSidebarComponent),
        forwardRef(() => ProductViewSummaryComponent),
        forwardRef(() => ProductViewTrayComponent),
        forwardRef(() => ProductViewWishlistComponent),
        RelatedProductsComponent,
        SizeClassDirective,
        ShellComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<ProductViewLayoutComponent>(PRODUCT_VIEW_LAYOUT_CONFIG)
export class ProductViewLayoutComponent extends ProductViewLayoutAbstract implements OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: IProductViewLayoutContent;

    horizontalImageScroll = signal<number>(-1);

    protected abIfService = inject(AbIfService);

    override ngOnInit(): void {
        this.subscriptions.add(
            this.abIfService.getState('(horizontalImageScroll) B').subscribe(value => {
                const currentVariant = this.horizontalImageScroll();
                const newValue = value ? 1 : 0;

                if (newValue !== currentVariant) {
                    this.horizontalImageScroll.set(newValue);
                    this.detectChanges();
                }
            })
        );

        super.ngOnInit();
    }
}
